import React from 'react';
import _ from 'lodash';

import FieldTreeWorkersApi from '../services/fieldTreeWorkers/FieldTreeWorkersApi';
import { FieldTreeWorkerManagerBase } from './FieldTreeWorkerManagerBase';
import { Confirmation, Information } from '../components/modals/common/';
import { Description } from '../components/Description';
import InfinityScrollLoad from '../components/InfinityScrollLoad';
import {
  PartnerCompanyBranchDropdown,
  HEAD_OFFICE_OPTION,
} from './PartnerCompanyBranchDropdown';
import Tippy from './ReactTippy';
import { isDeletedFieldTreeError } from '../utilities/apiErrorMessage';
import { messageUtility } from '../utilities/messageUtility';
import {
  WORKER_STATUS,
  CALL_FROM_PAGE,
  CCUS_SYNCED_STATUS,
} from '../utilities/constants';
import Styles from '../styles/componentStyle/FieldTreeGeneralWorkerManager/fieldTreeGeneralWorkerManager.scss';
import { ToolTip } from './ReactTippy';

/**
 * 画面上のTableの種別を表す定数
 */
const TABLE_KIND = {
  // 未割り当て作業員
  UNASSIGNED: 'unassigned',
  // 割り当て済み作業員
  ASSIGNED: 'assigned',
};
const CHANGE_STATUS = 'change_status';

const FieldTreeGeneralMessageConstants = {
  NO_UNASSIGNED_WORKERS: '登録できる作業員がいません',
  NO_ASSIGNED_WORKERS: '登録済み作業員がいません',
  FAIL_ASSIGNATION: '作業員の登録に失敗しました',
  FAIL_REMOVE_ASSIGNATION: '作業員の登録解除に失敗しました',
  FAIL_TO_GET_UNASSIGNED_WORKERS: '登録できる作業員の取得に失敗しました',
  FAIL_TO_GET_ASSIGNED_WORKERS: '登録済み作業員の取得に失敗しました',
  NO_WORKER_SELECTED: '操作する作業員にチェックを入れてください',
};

const LOAD_WORKER_LIMIT = 300;

/**
 * 協力会社作業員追加
 */
export default class FieldTreeGeneralWorkerManager extends FieldTreeWorkerManagerBase {
  constructor(props) {
    super(props);

    this.unassignedScrollRef = null;
    this.assignedScrollRef = null;

    this.state[TABLE_KIND.UNASSIGNED] = {
      searchConditions: {
        workerName: '',
        partner_company_branch_id: '',
      },
      isSelectAllChecked: false,
      isLoaded: false,
      offset: 0,
      inactiveWorkers: [],
    };
    this.state[TABLE_KIND.ASSIGNED] = {
      searchConditions: {
        workerName: '',
        partner_company_branch_id: '',
      },
      isSelectAllChecked: false,
      isLoaded: false,
      offset: 0,
    };
    this.state[CHANGE_STATUS] = {
      selectWorkerStatus: 0,
      fieldTreeWorkerId: '',
    };
    this.state['listFailedWorkers'] = [];

    this.events = {
      search: {
        // 検索条件セット
        set: this.setSearchCondition,
        // 検索実行
        gets: this.searchWorker,
      },
      onSelectAllChanged: this.onSelectAllChanged,
    };
  }

  /**
   * 未登録作業員のデータをロードする
   * @returns {Promise<void>}
   * @param param 検索用パラメーター
   */
  loadUnassignedWorker = async (param = {}) => {
    param.call_from = CALL_FROM_PAGE.FIELD_TREE_PAGE;

    if (_.isArray(this.state.worker) && param.isScroll) {
      param.offset = _.size(_.filter(this.state.worker, worker => {
        return !worker.isMoved; // 左右移動による作業員を除いてオフセットを求める
      }));
      if (param.offset === this.state[TABLE_KIND.UNASSIGNED].offset) {
        param.offset = _.size(this.state.worker);
        if (param.offset >= this.state.workerTotal) {
          param.offset = this.state.workerTotal;
        }
      }
    } else {
      param.offset = 0;
    }

    const data = await FieldTreeWorkersApi.getUnassignedWorker(
      this.state.field_tree_id,
      param
    ).catch(error => {
      this.stopScroll(param, TABLE_KIND.UNASSIGNED);
      this.prepareErrorDialog(
        error,
        FieldTreeGeneralMessageConstants.FAIL_TO_GET_UNASSIGNED_WORKERS
      );
    });
    // .catchが実行されなかった場合
    if (data) {
      // 正常の場合
      if (data.result) {
        if (data.data) {
          let workers = data.data.entities.unassigned_workers;
          _.each(workers, e => {
            e.checked = false;
          });
          let loadedWorkers;
          let isLoaded;
          if (param.isScroll) {
            loadedWorkers = this.loadedWorkers(workers, TABLE_KIND.UNASSIGNED);
            isLoaded = param.offset === this.state.workerTotal || param.offset === this.state[TABLE_KIND.UNASSIGNED].offset || _.isUndefined(workers);
          } else {
            loadedWorkers = workers;
            isLoaded = false;
          }
          this.setState({
            [TABLE_KIND.ASSIGNED]: {
              ...this.state[TABLE_KIND.ASSIGNED],
              isSelectAllChecked: false,
            },
            [TABLE_KIND.UNASSIGNED]: {
              ...this.state[TABLE_KIND.UNASSIGNED],
              isSelectAllChecked: false,
              isLoaded,
              offset: param.offset,
            },
            worker: loadedWorkers,
            workerTotal: data.data.total,
          });
        } else {
          this.stopScroll(param, TABLE_KIND.UNASSIGNED);
        }
      } else {
        // エラーの場合
        this.setState({
          [TABLE_KIND.UNASSIGNED]: {
            ...this.state[TABLE_KIND.UNASSIGNED],
            searchConditions: {
              ...this.state[TABLE_KIND.UNASSIGNED].searchConditions,
              partner_company_branch_id: '',
            },
            isLoaded: true,
            offset: param.offset,
          },
        });
        this.prepareErrorDialog(
          data,
          FieldTreeGeneralMessageConstants.FAIL_TO_GET_UNASSIGNED_WORKERS
        );
      }
    } else {
      this.stopScroll(param, TABLE_KIND.UNASSIGNED);
    }
  };

  /**
   * 登録済作業員をロードする
   * @returns {Promise<void>}
   * @param param 検索用パラメーター
   */
  loadAssignedWorker = async (param = {}) => {
    // 作業員のステータスが『稼働中』『作業終了』『退職』のデータを取得する
    param.worker_status = [
      WORKER_STATUS.IN_OPERATION.ID,
      WORKER_STATUS.WORK_DONE.ID,
      WORKER_STATUS.RETIREMENT.ID,
    ];
    param.call_from = CALL_FROM_PAGE.FIELD_TREE_PAGE;

    if (_.isArray(this.state.assignedWorker) && param.isScroll) {
      param.offset = _.size(_.filter(this.state.assignedWorker, worker => {
        return !worker.isMoved; // 左右移動による作業員を除いてオフセットを求める
      }));
      if (param.offset === this.state[TABLE_KIND.ASSIGNED].offset) {
        param.offset = _.size(this.state.assignedWorker);
        if (param.offset >= this.state.assignedWorkerTotal) {
          param.offset = this.state.assignedWorkerTotal;
        }
      }
    } else {
      param.offset = 0;
    }

    const data = await FieldTreeWorkersApi.getAssignedWorker(
      this.state.field_tree_id,
      param
    ).catch(error => {
      this.stopScroll(param, TABLE_KIND.ASSIGNED);
      this.prepareErrorDialog(
        error,
        FieldTreeGeneralMessageConstants.FAIL_TO_GET_ASSIGNED_WORKERS
      );
    });

    // .catchが実行されなかった場合
    if (data) {
      // 正常の場合
      if (data.result) {
        if (data.data) {
          let workers = data.data.entities.assigned_workers;
          _.each(workers, e => {
            e.checked = false;
          });
          let loadedWorkers;
          let isLoaded;
          if (param.isScroll) {
            loadedWorkers = this.loadedWorkers(workers, TABLE_KIND.ASSIGNED);
            isLoaded = param.offset === this.state.assignedWorkerTotal || param.offset === this.state[TABLE_KIND.ASSIGNED].offset || _.isUndefined(workers);
          } else {
            loadedWorkers = workers;
            isLoaded = false;
          }
          this.setState({
            [TABLE_KIND.ASSIGNED]: {
              ...this.state[TABLE_KIND.ASSIGNED],
              isSelectAllChecked: false,
              isLoaded,
              offset: param.offset,
            },
            [TABLE_KIND.UNASSIGNED]: {
              ...this.state[TABLE_KIND.UNASSIGNED],
              isSelectAllChecked: false,
            },
            assignedWorker: loadedWorkers,
            assignedWorkerTotal: data.data.total,
          });
        } else {
          this.stopScroll(param, TABLE_KIND.ASSIGNED);
        }
        return true;
      } else {
        // エラーの場合
        this.setState({
          [TABLE_KIND.ASSIGNED]: {
            ...this.state[TABLE_KIND.ASSIGNED],
            searchConditions: {
              ...this.state[TABLE_KIND.ASSIGNED].searchConditions,
              partner_company_branch_id: '',
            },
            isLoaded: true,
            offset: param.offset,
          },
        });
        this.prepareErrorDialog(
          data,
          FieldTreeGeneralMessageConstants.FAIL_TO_GET_ASSIGNED_WORKERS
        );
        return false;
      }
    } else {
      this.stopScroll(param, TABLE_KIND.ASSIGNED);
    }
    return false;
  };

  /**
   * 読み込み済みの作業員リスト
   * 読み込み済みの作業員リストに抽出した作業員をユニークに追加して返す
   * @param {array} workers 抽出した作業員
   * @param {string} tableKind 画面上のTableの種別
   * @return {array} 読み込み済みの作業員リスト
   */
  loadedWorkers = (workers, tableKind) => {
    const state = _.cloneDeep(this.state);
    let targetWorkers;
    if (tableKind === TABLE_KIND.ASSIGNED) {
      targetWorkers = state.assignedWorker;
    } else {
      targetWorkers = state.worker;
    }
    const intersectMovedWorkers = _.intersectionWith(targetWorkers, workers, (arrVal, othVal) => { return arrVal.isMoved && arrVal.id === othVal.id; });
    if (_.size(intersectMovedWorkers) > 0) {
      const intersectMovedIds = intersectMovedWorkers.map(w => w.id);
      _.each(targetWorkers, worker => {
        if (_.includes(intersectMovedIds, worker.id)) {
          worker.isMoved = false; // 左右移動による作業員がロード済みになる場合は印をオフにする
        }
      });
    }
    return _.uniqWith(_.concat(targetWorkers, workers), (arrVal, othVal) => { return arrVal.id === othVal.id; });
  }

  /**
   * 作業員をロードする
   * @returns {Promise<void>}
   */
  async loadWorker() {
    await Promise.all([this.loadUnassignedWorker(), this.loadAssignedWorker()]);
  }

  /**
   * 全選択チェックボックスの値が変化したとき
   * @param event
   * @param tableKind
   */
  onSelectAllChanged = (event, tableKind) => {
    let targetWorkers;

    if (tableKind === TABLE_KIND.ASSIGNED) {
      targetWorkers = _.cloneDeep(this.state.assignedWorker);
    } else {
      targetWorkers = _.cloneDeep(this.state.worker);
    }

    const state = { ...this.state };

    const beSelectedAll = !state[tableKind].isSelectAllChecked;
    state[tableKind].isSelectAllChecked = beSelectedAll;

    targetWorkers = _.map(targetWorkers, worker => {
      worker['checked'] = beSelectedAll;
      return worker;
    });

    if (tableKind === TABLE_KIND.ASSIGNED) {
      state.assignedWorker = targetWorkers;
    } else {
      state.worker = targetWorkers;
    }

    this.setState(state);
  };

  /**
   * テーブルのデータを左に移動する前に呼ばれる処理
   * @param items
   * @returns {Promise<boolean>}
   */
  doBeforeMoveItemsToLeft = async items => {
    if (items.length === 0) {
      this.showNoWorkerSelectedErrorMessage();
      return Promise.resolve();
    }
    return await this.deleteAssigners(items);
  };

  /**
   * 左へ移動
   */
  moveToLeft = () => {
    // 連打防止チェック
    if (this.state.isMoveInProgress) {
      return;
    }
    this.setState({
      isMoveInProgress: true,
    }, () => {
      //移動するデータ
      let move = _.filter(this.state.assignedWorker, worker => {
        return worker.checked == true;
      });
      let _assignedWorker = _.cloneDeep(this.state.assignedWorker);

      _.each(move, worker => {
        worker.checked = false;
      });
      // console.log('移動するデータ', move);

      this.doBeforeMoveItemsToLeft(move).then(result => {
        if (!result) {
          // 失敗したらチェック状態をもとに戻す
          this.setState({ isMoveInProgress: false }, () => {
            _.each(move, worker => {
              worker.checked = true;
            });
          })
          return;
        }
        // 右側のリスト（現場に登録中作業員）を設定する
        // 既存の右側のリストにて、移動（右→左）リストを除外する
        _.remove(_assignedWorker, worker => {
          const target = move.find(cmp => cmp.id === worker.id);
          if (target) {
            return true;
          }
        });

        const moveSizeBeforeFilter = _.size(move);

        // 退職作業員は左に追加するリストから除外
        move = _.filter(move, worker => {
          return WORKER_STATUS.RETIREMENT.ID !== worker.worker_status;
        });

        //左に追加
        //左側には移動（右→左）リストを追加する
        let _worker = _.cloneDeep(this.state.worker);
        this.setState({
          worker: _.concat(
            _.map(move, worker => {
              worker.checked = false;
              worker.isMoved = true; // 左右移動によるオブジェクトに印を付ける
              worker.jobs = [];
              delete worker.jobs;
              return worker;
            }),
            _worker
          ),
          assignedWorker: _assignedWorker,
          workerTotal: (this.state.workerTotal || 0) + _.size(move),
          assignedWorkerTotal: (this.state.assignedWorkerTotal || 0) - moveSizeBeforeFilter,
          isMoveInProgress: false,
        }, () => {
          if (this.state.assignedWorkerTotal > this.state.assignedWorker.length) {
            this.searchWorker(TABLE_KIND.ASSIGNED);
          }
        });
      });
    });
  };

  /**
   * 登録対象者をAPIに送信する
   * @param assigners
   */
  postAssigners = async assigners => {
    const fieldId = this.state.field_id;
    const fieldTreeId = this.state.field_tree_id;
    const workers = _.map(assigners, worker => {
      return { worker_id: worker.id };
    });
    const postData = {
      workers,
    };

    const data = await FieldTreeWorkersApi.postAssign(
      fieldId,
      fieldTreeId,
      postData
    ).catch(error => {
      this.prepareErrorDialog(
        error,
        FieldTreeGeneralMessageConstants.FAIL_ASSIGNATION
      );
    });

    // .catchが実行されなかった場合
    if (data) {
      // 正常の場合
      if (data.result) {
        let assignedWorkers = data.data.entities.assinged_workers;

        //返ってきたfield_tree_worker_id をエンティティに付与する。
        _.each(assignedWorkers, e => {
          const target = assigners.find(cmp => cmp.id === e.worker_id);
          if (target) {
            target.field_tree_worker_id = e.id;
            target.worker_status = WORKER_STATUS.IN_OPERATION.ID;
          }
        });
        this.setState({
          [TABLE_KIND.ASSIGNED]: {
            ...this.state[TABLE_KIND.ASSIGNED],
            isSelectAllChecked: false,
          },
          [TABLE_KIND.UNASSIGNED]: {
            ...this.state[TABLE_KIND.UNASSIGNED],
            isSelectAllChecked: false,
          },
        });
        return true;
      } else {
        // レスポンス結果より、登録された作業員情報を取得
        const assingedWorkers = _.get(data, 'data.entities.assinged_workers');
        if (!assingedWorkers) {
          if (isDeletedFieldTreeError(_.get(data, 'data.message'), this.state.field_tree_id)) {
            this.showErrorDialog(_.get(data, 'data.message'));
          } else {
            this.prepareErrorDialog(
              {status: data.status},
              messageUtility.getMessage('E-001-437'),
              true
            );
          }
          return false;
        }

        //返ってきたfield_tree_worker_id をエンティティに付与する。
        _.each(assingedWorkers, e => {
          const target = assigners.find(cmp => cmp.id === e.worker_id);
          if (target) {
            target.field_tree_worker_id = e.id;
            target.worker_status = WORKER_STATUS.IN_OPERATION.ID;
          }
        });

        // 登録されない作業員情報は移動リスト（左→右）から除外する
        _.remove(assigners, e => {
          const target = assingedWorkers.find(cmp => cmp.worker_id === e.id);
          if (!target) {
            return true;
          }
        });
        this.prepareErrorDialog(
          data,
          FieldTreeGeneralMessageConstants.FAIL_ASSIGNATION,
          true
        );
        // 一部だけ登録に成功した場合も正常扱いとしてtrueを返す
        this.setState({
          [TABLE_KIND.ASSIGNED]: {
            ...this.state[TABLE_KIND.ASSIGNED],
            isSelectAllChecked: false,
          },
          [TABLE_KIND.UNASSIGNED]: {
            ...this.state[TABLE_KIND.UNASSIGNED],
            isSelectAllChecked: false,
          },
        });
        return true;
      }
    }
    return false;
  };

  /**
   * 作業員ステータスを変更する。
   */
  changeStatus = () => {
    const state = { ...this.state };
    if (
      WORKER_STATUS.WORK_DONE.ID === state[CHANGE_STATUS].selectWorkerStatus
    ) {
      FieldTreeWorkersApi.inactive({
        field_tree_worker_ids: [state[CHANGE_STATUS].fieldTreeWorkerId],
      })
        .then(res => {
          if (res.result) {
            let success = res.data.success;
            success.forEach(fieldTreeWorkerId => {
              _.map(state.assignedWorker, worker => {
                if (fieldTreeWorkerId === worker.field_tree_worker_id) {
                  worker.worker_status = WORKER_STATUS.WORK_DONE.ID;
                }
              });
            });
            this.setState(state);
            this.closeStatusDialog();
          } else {
            this.prepareErrorDialog(res);
          }
        })
        .catch(error => {
          this.prepareErrorDialog(error);
        });
    } else if (
      WORKER_STATUS.IN_OPERATION.ID === state[CHANGE_STATUS].selectWorkerStatus
    ) {
      FieldTreeWorkersApi.active({
        field_tree_worker_ids: [state[CHANGE_STATUS].fieldTreeWorkerId],
      })
        .then(res => {
          if (res.result) {
            let success = res.data.success;
            success.forEach(fieldTreeWorkerId => {
              _.map(state.assignedWorker, worker => {
                if (fieldTreeWorkerId === worker.field_tree_worker_id) {
                  worker.worker_status = WORKER_STATUS.IN_OPERATION.ID;
                }
              });
            });
            this.setState(state);
            this.closeStatusDialog();
          } else {
            this.prepareErrorDialog(res);
          }
        })
        .catch(error => {
          this.prepareErrorDialog(error);
        });
    }
  };

  /**
   * テーブルのデータを右に移動する前に呼ばれる処理
   * @param items
   * @returns {Promise<boolean>}
   */
  doBeforeMoveItemsToRight = async items => {
    if (items.length === 0) {
      this.showNoWorkerSelectedErrorMessage();
      return Promise.resolve();
    }
    return await this.postAssigners(items);
  };

  /**
   * 右へ移動
   */
  moveToRight = () => {
    // 連打防止チェック
    if (this.state.isMoveInProgress) {
      return;
    }
    this.setState({
      isMoveInProgress: true,
    }, () => {
      //移動するデータ
      let move = _.filter(this.state.worker, worker => {
        return worker.checked == true;
      });
      let _worker = _.cloneDeep(this.state.worker);

      _.each(move, worker => {
        worker.checked = false;
      });
      // console.log('移動するデータ', move);

      this.doBeforeMoveItemsToRight(move).then(result => {
        if (!result) {
          // 失敗したらチェック状態をもとに戻す
          this.setState({ isMoveInProgress: false }, () => {
            _.each(move, worker => {
              worker.checked = true;
            });
          })
          return;
        }
        //左から削除
        _.remove(_worker, worker => {
          const target = move.find(cmp => cmp.id === worker.id);
          if (target) {
            return true;
          }
        });

        //右に追加
        let _assignedWorker = _.cloneDeep(this.state.assignedWorker);
        this.setState({
          worker: _worker,
          assignedWorker: _.concat(
            _.map(move, worker => {
              worker.checked = false;
              worker.isMoved = true; // 左右移動によるオブジェクトに印を付ける
              return worker;
            }),
            _assignedWorker,
          ),
          workerTotal: (this.state.workerTotal || 0) - _.size(move),
          assignedWorkerTotal: (this.state.assignedWorkerTotal || 0) + _.size(move),
          isMoveInProgress: false,
        }, () => {
          if (this.state.workerTotal > this.state.worker.length) {
            this.searchWorker(TABLE_KIND.UNASSIGNED);
          }
        });
      });
    });
  };

  /**
   * 登録解除対象者をAPIに送信する
   * @param assigners
   */
  deleteAssigners = async assigners => {
    const fieldId = this.state.field_id;
    const fieldTreeId = this.state.field_tree_id;
    const workers = _.map(assigners, worker => {
      return {
        worker_id: worker.id,
        field_tree_worker_id: worker.field_tree_worker_id,
      };
    });
    const postData = {
      workers,
    };

    const data = await FieldTreeWorkersApi.deleteAssign(
      fieldId,
      fieldTreeId,
      postData
    ).catch(error => {
      this.prepareErrorDialog(
        error,
        FieldTreeGeneralMessageConstants.FAIL_REMOVE_ASSIGNATION
      );
    });
    // .catchが実行されなかった場合
    if (data) {
      if (data.result) {
        _.each(assigners, e => {
          delete e.field_tree_worker_id;
        });
        this.setState({
          [TABLE_KIND.ASSIGNED]: {
            ...this.state[TABLE_KIND.ASSIGNED],
            isSelectAllChecked: false,
          },
          [TABLE_KIND.UNASSIGNED]: {
            ...this.state[TABLE_KIND.UNASSIGNED],
            isSelectAllChecked: false,
          },
        });
        return true;
      } else if (data.result == undefined && data.data.entities == undefined) {
        if (isDeletedFieldTreeError(_.get(data, 'data.message'), this.state.field_tree_id)) {
          this.showErrorDialog(_.get(data, 'data.message'));
        } else {
          this.prepareErrorDialog(
            {status: data.status},
            messageUtility.getMessage('E-001-437'),
            true
          );
        }
        return false;
      } else {
        // レスポンス結果より、解除された作業員情報を取得
        let unassignedWorkers = data.data.entities.unassinged_workers;
        const unassignedWorkersFailed = _.remove(
          unassignedWorkers,
          worker =>
            worker.has_check_in_out_log || worker.has_registered_in_greenfile
        );
        if (_.isEmpty(unassignedWorkersFailed)) {
          this.prepareErrorDialog(
            data,
            FieldTreeGeneralMessageConstants.FAIL_REMOVE_ASSIGNATION
          );
        } else {
          const listFailedWorkers = _.map(unassignedWorkersFailed, worker => {
            const target = _.find(assigners, { id: worker.worker_id });
            if (target) {
              let textError = '労務安全書類の登録があります';
              if (
                worker.has_check_in_out_log &&
                worker.has_registered_in_greenfile
              ) {
                textError = '入退場実績と労務安全書類の登録があります';
              } else if (
                worker.has_check_in_out_log &&
                !worker.has_registered_in_greenfile
              ) {
                textError = '入退場実績の登録があります';
              }
              return `・（${target.fullname}）：${textError}`;
            }
            return '';
          });
          this.setState({ listFailedWorkers });
        }
        // 一部だけ解除に成功した場合も正常扱いとしてtrueを返す
        if (!_.isEmpty(unassignedWorkers)) {
          // 解除されない作業員情報は移動リスト（右→左）から除外する
          _.remove(
            assigners,
            e => !_.find(unassignedWorkers, { worker_id: e.id })
          );
          this.setState({
            [TABLE_KIND.ASSIGNED]: {
              ...this.state[TABLE_KIND.ASSIGNED],
              isSelectAllChecked: false,
            },
            [TABLE_KIND.UNASSIGNED]: {
              ...this.state[TABLE_KIND.UNASSIGNED],
              isSelectAllChecked: false,
            },
          });
          return true;
        }
        return false;
      }
    }
    return false;
  };

  /**
   * 検索条件セット
   * @param event {object} イベント
   * @param tableKind 検索対象のテーブルの種別
   */
  setSearchCondition = (event, tableKind) => {
    const searchConditions = this.state[tableKind].searchConditions;
    searchConditions[event.target.name] = event.target.value;
    this.setState({ searchConditions });
  };

  /**
   * 検索パラメーターを生成する
   * @param tableKind
   * @returns {{name, partner_company_branch_id}}
   */
  generateSearchParam = tableKind => {
    let param = {
      name: this.state[tableKind].searchConditions.workerName,
      partner_company_branch_id: this.state[tableKind].searchConditions
        .partner_company_branch_id,
    };
    if (param.partner_company_branch_id === '') {
      delete param.partner_company_branch_id;
    }
    if (param.partner_company_branch_id === HEAD_OFFICE_OPTION.id) {
      param.partner_company_branch_id = '';
    }
    return param;
  };

  /**
   * @description ダイアログ表示
   * @param message
   */
  showErrorDialog = errorMessage => {
    this.handleVisibleDialog(true);
    this.setState({ errorMessage });
  };

  /**
   * エラーメッセージを設定する
   *  @param error レスポンスのエラー
   *  @param defaultMessage デフォルトのエラーメッセージ
   *  @param multilineTextFlag エラーメッセージをマルチライン表示するか
   */
  prepareErrorDialog = async (
    error,
    defaultMessage = '',
    multilineTextFlag = false
  ) => {
    this.handleMultilineTextFlag(multilineTextFlag);
    if (error.message) {
      this.showErrorDialog(error.message);
    } else if (_.has(error, 'data.message')) {
      this.showErrorDialog(error.data.message);
    } else {
      this.showErrorDialog(defaultMessage);
    }
    if (error.status && error.status != 403 && error.status != 200 &&
      ![FieldTreeGeneralMessageConstants.FAIL_TO_GET_UNASSIGNED_WORKERS,
        FieldTreeGeneralMessageConstants.FAIL_TO_GET_ASSIGNED_WORKERS].includes(defaultMessage)) {
      await this.loadWorker();
    }
  };

  /**
   * @description ステータス変更ダイアログ表示
   * @param fieldTreeWorkerId
   * @param selectWorkerStatus
   */
  showStatusDialog = (fieldTreeWorkerId, selectWorkerStatus) => {
    this.setState({
      [CHANGE_STATUS]: {
        ...this.state[CHANGE_STATUS],
        fieldTreeWorkerId,
        selectWorkerStatus,
      },
      visibleStatusDialog: true,
    });
  };

  /**
   * @description ステータスラジオボタン変更イベント
   * @param selectedStatus
   */
  checkStatus = selectedStatus => {
    this.setState({
      [CHANGE_STATUS]: {
        ...this.state[CHANGE_STATUS],
        selectWorkerStatus: selectedStatus,
      },
    });
  };

  /**
   * @description ステータス変更ダイアログを閉じる
   */
  closeStatusDialog = () => {
    this.setState({ visibleStatusDialog: false });
  };

  /**
   * @description ダイアログ開閉設定
   */
  handleVisibleDialog = visibleDialogFlag => {
    this.setState({
      visibleDialogFlag,
    });
  };

  /**
   * @description ダイアログメッセージ設定
   */
  handleMultilineTextFlag = multilineTextFlag => {
    this.setState({
      multilineTextFlag,
    });
  };

  /**
   * @description ダイアログ設定
   * @param message
   */
  getAlertModalForm(message) {
    const isDeletedFieldTree = isDeletedFieldTreeError(message, this.state.field_tree_id);
    let title;
    let buttonText = messageUtility.getMessage('I-000-011');
    if (isDeletedFieldTree) {
      title = messageUtility.getMessage('E-000-076');
      message = messageUtility.getMessage('E-000-075');
      buttonText = messageUtility.getMessage('I-000-017');
    }
    return (
      <Information
        title={title}
        message={message}
        buttonText={buttonText}
        visible={this.state.visibleDialogFlag}
        multilineText={this.state.multilineTextFlag}
        onClose={() => {
          isDeletedFieldTree
            ? window.location.reload()
            : this.handleVisibleDialog(false);
        }}
      />
    );
  }

  /**
   * @description ダイアログ設定
   * @param message
   */
  getStatusModalForm() {
    return (
      <Confirmation
        visible={this.state.visibleStatusDialog}
        title={data.statusDialog.title}
        confirmButtonText={data.statusDialog.buttonTitle.update}
        cancelButtonText={data.statusDialog.buttonTitle.back}
        isNegative={true}
        negativeButtonClassName={'btn--effective'}
        message={
          <StatusDialog
            fieldTreeWorkerId={this.state[CHANGE_STATUS].fieldTreeWorkerId}
            selectWorkerStatus={this.state[CHANGE_STATUS].selectWorkerStatus}
            checkStatus={this.checkStatus}
          />
        }
        rootClassName={Styles.statusDialog}
        onCancel={() => this.closeStatusDialog()}
        onConfirm={() => this.changeStatus()}
      />
    );
  }

  /**
   * 絞り込みフォーム
   * @param {int} loadedCount
   * @param {int} total
   * @param tableKind
   */
  getSearchForm(loadedCount, total, tableKind) {
    const SearchFormHeader = ({ loadedCount, total }) => (
      <div className='search-form__header'>
        <div className='search-form__title'>
          検索条件（検索結果
          <span className='bold'>
            {(total || 0) > LOAD_WORKER_LIMIT && (_.toString(loadedCount || 0) + ' / ')}
            {total || 0}
          </span>
          件)
        </div>
      </div>
    );

    return (
      <div className='master-slide-view__searchform'>
        <div className='search-form'>
          <SearchFormHeader loadedCount={loadedCount} total={total} />
          <div id='search-form-accordion' className='accordion show'>
            <div className='search-form__body'>
              <div className='form form--search-form'>
                <div className='form__group flex-wrap'>
                  <div className='form-element'>
                    <div className='form-element__label'>
                      <span className='label'>支店で検索</span>
                    </div>
                    <div className='form-element__element --col-xs-12 --col-md-12 --col-lg-4 --col-xl-4'>
                      <PartnerCompanyBranchDropdown
                        selectedCompanyId={this.state.company_id}
                        selectedPartnerCompanyBranchId={
                          this.state[tableKind].searchConditions
                            .partner_company_branch_id
                        }
                        onSelect={id => {
                          this.state[
                            tableKind
                          ].searchConditions.partner_company_branch_id = id;
                          this.events.search.gets(tableKind);
                        }}
                        fixedOptions={[HEAD_OFFICE_OPTION]}
                        params={{ call_from: CALL_FROM_PAGE.FIELD_TREE_PAGE }}
                        isOptional={true}
                        dropdownSelectStyle={{ width: '150px' }}
                      />
                    </div>
                  </div>
                  <div className='form-element'>
                    <div className='form-element__label'>
                      <span className='label'>氏名（カナ検索可）</span>
                    </div>
                    <div className='form-element__element --col-xs-12 --col-md-12 --col-lg-4 --col-xl-4'>
                      <input
                        type='text'
                        name='workerName'
                        placeholder='作業員名を入力'
                        value={
                          this.state[tableKind].searchConditions.workerName
                        }
                        onChange={event =>
                          this.events.search.set(event, tableKind)
                        }
                        onBlur={() => this.events.search.gets(tableKind)}
                      />
                    </div>
                  </div>
                </div>
                <div className='search-form__info'>
                  <div className='search-form__info-text'>
                    検索条件で支店を選択して作業員が見つからない場合、支店を未選択にし氏名のみで再度検索してください。
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /**
   * 作業員を検索する
   * @param tableKind
   */
  searchWorker = tableKind => {
    clearTimeout(this.inputTimer);
    this.inputTimer = setTimeout(() => {
      const param = this.generateSearchParam(tableKind);
      if (tableKind === TABLE_KIND.UNASSIGNED) {
        this.setState({
          [TABLE_KIND.UNASSIGNED]: {
            ...this.state[TABLE_KIND.UNASSIGNED],
            inactiveWorkers: [],
          },
        }, () => {
          this.loadUnassignedWorker(param);
        })
      } else {
        this.loadAssignedWorker(param);
      }
    }, 500);
  };

  /**
   * 作業員リストをスクロールする
   * @param {function} notify スクロールの終了通知
   * @param {string} tableKind 画面上のTableの種別
   */
  async scrollWorker(notify, tableKind) {
    const state = _.cloneDeep(this.state);
    if (state[tableKind].isLoaded && notify) {
      notify();
      return;
    }
    const param = this.generateSearchParam(tableKind);
    param.isScroll = true;
    if (tableKind === TABLE_KIND.UNASSIGNED) {
      await this.loadUnassignedWorker(param);
    } else {
      await this.loadAssignedWorker(param);
    }
    if (notify) {
      notify();
    }
  }

  /**
   * スクロールを終了する
   * @param {object} param APIに渡したパラメータ
   * @param {string} tableKind 画面上のTableの種別
   */
  stopScroll = (param, tableKind) => {
    if (!param.isScroll) {
      return;
    }
    const state = _.cloneDeep(this.state);
    state[tableKind].isLoaded = true;
    state[tableKind].offset = param.offset;
    this.setState(state);
  };

  /**
   * 未登録作業員のエレメントを取得する
   */
  getUnassignedWorkersRenderElement() {
    const style = {
      fontSize: '12px',
      color: '#7C7C7C',
      padding: '8px',
      textAlign: 'center',
    };
    if (!_.size(this.state.worker)) {
      return (
        <tr>
          <td style={style} colSpan='3'>
            {FieldTreeGeneralMessageConstants.NO_UNASSIGNED_WORKERS}
          </td>
        </tr>
      );
    }
    return _.map(this.state.worker, worker => {
      return (
        <UnassignedWorker
          key={`unassigned_${worker.id}`}
          data={worker}
          callback={this.pushCheckedWorker}
        />
      );
    });
  }

  /**
   * 登録済み作業員のエレメントを取得する
   */
  getAssignedWorkersRenderElement() {
    const style = {
      fontSize: '12px',
      color: '#7C7C7C',
      padding: '8px',
      textAlign: 'center',
    };
    if (!_.size(this.state.assignedWorker)) {
      return (
        <tr>
          <td style={style} colSpan='4'>
            {FieldTreeGeneralMessageConstants.NO_ASSIGNED_WORKERS}
          </td>
        </tr>
      );
    } else {
      return _.map(this.state.assignedWorker, worker => {
        return (
          <AssignedWorker
            key={`assigned_${worker.id}`}
            // fieldId={this.state.field_id}
            fieldTreeId={this.state.field_tree_id}
            data={worker}
            callback={this.pushCheckedAssignWorker}
            showStatusDialog={this.showStatusDialog}
          />
        );
      });
    }
  }

  /**
   * 未登録に戻す作業員チェック
   * @param {int} id
   * @param {int} checked
   */
  pushCheckedWorker = (id, checked) => {
    const worker = _.map(this.state.worker, item => {
      if (item.id === id) {
        item['checked'] = checked;
      }
      return item;
    });
    this.setState({ worker });
  };

  /**
   * 登録予定作業員チェック
   * @param {int} id
   * @param {int} checked
   */
  pushCheckedAssignWorker = (id, checked) => {
    const assignedWorker = _.map(this.state.assignedWorker, item => {
      if (item.id === id) {
        item['checked'] = checked;
      }
      return item;
    });
    this.setState({ assignedWorker });
  };

  /**
   * 作業員が選択されてないとき用のメッセージを表示する。
   */
  showNoWorkerSelectedErrorMessage = () => {
    this.setState({ isMoveInProgress: false }, () => {
      this.handleMultilineTextFlag(false);
      this.showErrorDialog(FieldTreeGeneralMessageConstants.NO_WORKER_SELECTED);
    });
  };

  /**
   * CCUS連携チェックと注意表示
   * @param stateData
   * @param {boolean} stateData.autoTaskActive 自動連携状態、null = 連携設定なし
   */
  showCcusWarning(stateData) {
    const {
      autoTaskActive,
      companySecededCcusFlag,
      ccusSyncedStatus,
      jvFlag,
    } = stateData;

    // 現場がCCUS連携する設定になってなる場合、自動連携状態がtrue/falseでも注意メッセージを表示させる
    if (!_.isNil(autoTaskActive)) {
      if (
          companySecededCcusFlag ||
          ccusSyncedStatus === CCUS_SYNCED_STATUS.SECEDED.ID
      ) {
        const message = companySecededCcusFlag
          ? messageUtility.getMessage(jvFlag ? 'I-000-239' : 'I-000-247')
          : messageUtility.getMessage('I-000-240');
        return (
            <div className='WorkerManageDialog__description'>
              <Description type='primary'>{message}</Description>
            </div>
        );
      }

      return (
        <div className='WorkerManageDialog__description'>
          <Description type='primary'>
            <span className='text--warning'>
              エラーが発生するためCCUS側で作業員の追加や削除は行わないでください。
            </span>
            現場に追加した作業員の情報はCCUSへ連携されます。
          </Description>
        </div>
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_.isEqual(prevState.worker, this.state.worker)) {
      const activeWorkers = _.filter(this.state.worker, worker => !worker.inactive_flag);
      const newInactiveWorkers = _.filter(this.state.worker, worker => worker.inactive_flag);
      const inactiveWorkers = _.uniqBy([...this.state[TABLE_KIND.UNASSIGNED].inactiveWorkers, ...newInactiveWorkers], 'id');
      this.setState({
        [TABLE_KIND.UNASSIGNED]: {
          ...this.state[TABLE_KIND.UNASSIGNED],
          inactiveWorkers,
        },
        worker: activeWorkers,
      });
    }
  }

  render() {
    const inactiveWorkersLength = _.get(this.state[TABLE_KIND.UNASSIGNED], 'inactiveWorkers.length', 0);
    const activeUnassignedWorkers = this.state.workerTotal - inactiveWorkersLength;
    return (
      <div
        className={`${
          Styles.dialog
        } dialog dialog-md dialog--modal dialog--dialog-worker-manage dialog--general-worker-manage`}
      >
        <div className='dialog-md__header'>
          <span className='dialog__title company-name'>
            {this.state.company_name}
          </span>
          <span className='dialog__title'>の作業員を追加</span>
        </div>
        <div className='dialog__body'>
          {this.showCcusWarning(this.props.dialog.state)}
          <div className='dual-panel'>
            <div className='dual-panel__item'>
              <div className='panel'>
                <div className='panel-header'>
                  <div className='panel-title'>現場に未登録</div>
                  {this.getSearchForm(
                    this.state.worker.length,
                    activeUnassignedWorkers,
                    TABLE_KIND.UNASSIGNED
                  )}
                  <div className='table-wrapper'>
                    <div
                      ref={ref => {
                        this.unassignedScrollRef = ref;
                      }}
                      className='panel-list worker-manage-table' id='infinityscroll-target-unassigned'
                    >
                      <table className='table table--worker-manage-label unassigned-table'>
                        <thead className='table__header'>
                          <tr className='table__label'>
                            <th className='value-table select-all-check'>
                              <label className='checkbox'>
                                <input
                                  name='checkbox1'
                                  type='checkbox'
                                  checked={
                                    this.state[TABLE_KIND.UNASSIGNED]
                                      .isSelectAllChecked
                                  }
                                  onChange={event =>
                                    this.events.onSelectAllChanged(
                                      event,
                                      TABLE_KIND.UNASSIGNED
                                    )
                                  }
                                />
                                {renderCheckAllCheckboxSquare(this.state.workerTotal)}
                              </label>
                            </th>
                            <th className='value-table value-table_branch value-table--table__label'>
                              <span className='value-table__label'>支店</span>
                            </th>
                            <th className='value-table value-table--table__label'>
                              <span className='value-table__label'>
                                {data.tableTitle.fullName}
                              </span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className='table__body'>
                          {this.getUnassignedWorkersRenderElement()}
                        </tbody>
                      </table>
                      <InfinityScrollLoad
                        targetId={'infinityscroll-target-unassigned'}
                        total={_.size(this.state.worker)}
                        no_data_message={' '}
                        callback={notifyEnd => {
                          this.scrollWorker(notifyEnd, TABLE_KIND.UNASSIGNED);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='dual-panel__navi dual-panel__navi__worker'>
              <div className='dual-panel__navi-item'>
                <button
                  className='btn-move__right'
                  onClick={() => {
                    this.unassignedScrollRef.scrollTop = 0;
                    this.moveToRight();
                  }}
                  disabled={this.state.isMoveInProgress}
                />
              </div>
              <div className='dual-panel__navi-spacer' />
              <div className='dual-panel__navi-item'>
                <button
                  className='btn-move__left'
                  onClick={() => {
                    this.assignedScrollRef.scrollTop = 0;
                    this.moveToLeft();
                  }}
                  disabled={this.state.isMoveInProgress}
                />
              </div>
            </div>
            <div className='dual-panel__item'>
              <div className='panel'>
                <div className='panel-header'>
                  <div className='panel-title'>現場に登録中</div>
                  {this.getSearchForm(
                    this.state.assignedWorker.length,
                    this.state.assignedWorkerTotal,
                    TABLE_KIND.ASSIGNED
                  )}
                  <div className='table-wrapper'>
                    <div
                      ref={ref => {
                        this.assignedScrollRef = ref;
                      }}
                      className='panel-list worker-manage-table' id='infinityscroll-target-assigned'
                    >
                      <table className='table table--worker-manage-label assigned-table'>
                        <thead className='table__header'>
                          <tr className='table__label'>
                            <td className='value-table select-all-check'>
                              <label className='checkbox'>
                                <input
                                  name='checkbox1'
                                  type='checkbox'
                                  checked={
                                    this.state[TABLE_KIND.ASSIGNED]
                                      .isSelectAllChecked
                                  }
                                  onChange={event =>
                                    this.events.onSelectAllChanged(
                                      event,
                                      TABLE_KIND.ASSIGNED
                                    )
                                  }
                                />
                                {renderCheckAllCheckboxSquare(this.state.assignedWorkerTotal)}
                              </label>
                            </td>
                            <td className='value-table value-table_branch value-table--table__label'>
                              <span className='value-table__label'>支店</span>
                            </td>
                            <td className='value-table value-table--table__label'>
                              <span className='value-table__label'>
                                {data.tableTitle.fullName}
                              </span>
                            </td>
                            <td className='value-table value-table--table__label'>
                              <span className='value-table__label'>
                                {data.tableTitle.status}
                              </span>
                            </td>
                          </tr>
                        </thead>
                        <tbody className='table__body'>
                          {this.getAssignedWorkersRenderElement()}
                        </tbody>
                      </table>
                      <InfinityScrollLoad
                        targetId={'infinityscroll-target-assigned'}
                        total={_.size(this.state.assignedWorker)}
                        no_data_message={' '}
                        callback={notifyEnd => {
                          this.scrollWorker(notifyEnd, TABLE_KIND.ASSIGNED);
                        }}
                      />
                    </div>
                    <div className='panel-message'>
                      {data.message.statusInfo}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {this.getAlertModalForm(this.state.errorMessage)}
        {this.getStatusModalForm()}
        <div className='dialog-md__footer-horizontal'>
          <button
            className='btn-fixedsize--sub'
            onClick={() => {
              this.props.dialog.callback.back();
            }}
          >
            戻る
          </button>
          <button
            className={`${
              _.filter(this.state.assignedWorker, it => {
                return it.worker_status === WORKER_STATUS.IN_OPERATION.ID;
              }).length < 1
                ? 'btn-disable'
                : 'btn-fixedsize'
            }`}
            disabled={
              _.filter(this.state.assignedWorker, it => {
                return it.worker_status === WORKER_STATUS.IN_OPERATION.ID;
              }).length < 1
            }
            onClick={() => {
              this.props.dialog.callback.submit();
            }}
          >
            職種・立場登録
          </button>
        </div>
        <Information
          visible={!_.isEmpty(this.state.listFailedWorkers)}
          title='失敗しました'
          message={
            <div className='dialog--workers-failed__content'>
              データが登録されている作業員は現場から外すことができません。
              <br />
              現場作業が終了している場合は、ステータスを「作業終了」に変更してください。
              <br />
              <br />
              現場から外す場合には以下の操作が必要です。
              <br />
              入退場実績の登録がある場合：すべての入退場実績を削除してください。
              <br />
              労務安全書類の登録がある場合：再下請負通知書の主任技術者・登録基幹技能者・専門技術者から解除してください。
              <br />
              <div className='dialog--workers-failed__list'>
                {_.map(this.state.listFailedWorkers, (worker, index) => (
                  <div key={index}>{worker}</div>
                ))}
              </div>
            </div>
          }
          buttonText={messageUtility.getMessage('I-000-017')}
          onClose={() => this.setState({ listFailedWorkers: [] })}
          rootClassName='dialog--wide dialog--workers-failed'
        />
      </div>
    );
  }
}

/**
 * 全てチェックボックス
 * total件数がリミットを超える場合は、ツールチップを表示する
 * @param {number} total total件数
 */
const renderCheckAllCheckboxSquare = total => {
  return <Tippy
    delay={[500, 0]}
    duration={10}
    arrow={true}
    arrowType='sharp'
    placement='bottom'
    disable={(total || 0) <= LOAD_WORKER_LIMIT}
    html={
      <React.Fragment>
        <ToolTip>作業員は{LOAD_WORKER_LIMIT}件ずつ読み込みがされます。<br />読み込み済の作業員だけが一括選択されます。</ToolTip>
      </React.Fragment>
    }
  >
    <i className='i-square i-2x' />
  </Tippy>;
};

/**
 * =====================================================
 * 未登録ユーザーのコンポーネント
 * =====================================================
 */
class UnassignedWorker extends React.Component {
  constructor(props) {
    super(props);
    this.uid = `check${_.random(999999)}`;
    this.checkRef = null;
  }

  getValue() {
    this.checkRef.checked = !this.checkRef.checked;
    if (this.props.callback) {
      this.props.callback(this.props.data.id, this.checkRef.checked);
    }
  }

  render() {
    const isInactive = this.props.data.inactive_flag;
    return (
      <tr
        className={isInactive ? 'table__item table__item--inactive' : 'table__item'}
        key={this.uid}
        onClick={() => {
          this.getValue();
        }}
      >
        <td className='value-table'>
          <div className='value-table__icon'>
            <div className='btn-checkbox-mini'>
              <label htmlFor={this.uid} className='checkbox'>
                <input
                  ref={checkbox => {
                    this.checkRef = checkbox;
                  }}
                  id={this.uid}
                  type='checkbox'
                  name={this.props.data.id}
                  checked={this.props.data.checked}
                  readOnly
                />
                <i className='i-square i-2x' />
              </label>
            </div>
          </div>
        </td>
        <td className='value-table'>
          <Tippy
            delay={[500, 0]}
            duration={10}
            arrow={true}
            arrowType='sharp'
            placement='bottom'
            html={renderTooltipContent(
              _.get(this.props, 'data.partner_company_branch.name', '本社')
            )}
          >
            <div className='value-table__name'>
              {_.get(this.props, 'data.partner_company_branch.name', '本社')}
            </div>
          </Tippy>
        </td>
        <td className='value-table'>
          <Tippy
            delay={[500, 0]}
            duration={10}
            arrow={true}
            arrowType='sharp'
            placement='bottom'
            html={renderTooltipContent(this.props.data.fullname)}
          >
            <div className='value-table__name'>{this.props.data.fullname}</div>
          </Tippy>
        </td>
      </tr>
    );
  }
}

/**
 * =====================================================
 * 登録済み作業員のコンポーネント
 * =====================================================
 */
class AssignedWorker extends React.Component {
  constructor(props) {
    super(props);
    this.uid = `check${_.random(999999)}`;
    this.checkRef = null;
  }

  statusIcons = workerStatus => {
    if (WORKER_STATUS.RETIREMENT.ID === workerStatus) {
      return <div className='status--mid'>{WORKER_STATUS.RETIREMENT.NAME}</div>;
    } else if (WORKER_STATUS.WORK_DONE.ID === workerStatus) {
      return (
        <div
          className='status--mid editable'
          onClick={e => {
            e.stopPropagation();
            if (this.props.showStatusDialog) {
              this.props.showStatusDialog(
                this.props.data.field_tree_worker_id,
                this.props.data.worker_status
              );
            }
          }}
        >
          {WORKER_STATUS.WORK_DONE.NAME}
          <i className='i-arrow-small-bottom--dark' />
        </div>
      );
    } else {
      return (
        <div
          className='status--mid--caution editable'
          onClick={e => {
            e.stopPropagation();
            if (this.props.showStatusDialog) {
              this.props.showStatusDialog(
                this.props.data.field_tree_worker_id,
                this.props.data.worker_status
              );
            }
          }}
        >
          {WORKER_STATUS.IN_OPERATION.NAME}
          <i className='i-arrow-small-bottom--dark' />
        </div>
      );
    }
  };

  getValue() {
    this.checkRef.checked = !this.checkRef.checked;
    if (this.props.callback) {
      this.props.callback(this.props.data.id, this.checkRef.checked);
    }
  }

  render() {
    return (
      <tr
        className='table__item'
        key={this.uid}
        onClick={() => {
          this.getValue();
        }}
      >
        <td className='value-table'>
          <div className='value-table__icon'>
            <div className='btn-checkbox-mini'>
              <label htmlFor={this.uid} className='checkbox'>
                <input
                  ref={checkbox => {
                    this.checkRef = checkbox;
                  }}
                  id={this.uid}
                  type='checkbox'
                  name={this.props.data.id}
                  checked={this.props.data.checked}
                  readOnly
                />
                <i className='i-square i-2x' />
              </label>
            </div>
          </div>
        </td>
        <td className='value-table'>
          <Tippy
            delay={[500, 0]}
            duration={10}
            arrow={true}
            arrowType='sharp'
            placement='bottom'
            html={renderTooltipContent(
              _.get(this.props, 'data.partner_company_branch.name', '本社')
            )}
          >
            <div className='value-table__name'>
              {_.get(this.props, 'data.partner_company_branch.name', '本社')}
            </div>
          </Tippy>
        </td>
        <td className='value-table'>
          <Tippy
            delay={[500, 0]}
            duration={10}
            arrow={true}
            arrowType='sharp'
            placement='bottom'
            html={renderTooltipContent(this.props.data.fullname)}
          >
            <div className='value-table__name'>{this.props.data.fullname}</div>
          </Tippy>
        </td>
        <td className='value-table'>
          <div className='value-table__worker_status'>
            {this.statusIcons(this.props.data.worker_status)}
          </div>
        </td>
      </tr>
    );
  }
}

/**
 * Tooltipで表示される文字
 * @param {string} data 表示データ
 */
const renderTooltipContent = data => {
  const customStyle = {
    maxWidth: '240px',
  };

  return <ToolTip tooltipStyle={customStyle}>{data}</ToolTip>;
};

/**
 * =====================================================
 * 作業員ステータス変更ダイアログ
 * =====================================================
 */
class StatusDialog extends React.Component {
  constructor(props) {
    super(props);
    this.selectValue = this.props.selectWorkerStatus;
  }

  checkStatus = selectValue => {
    this.selectValue = Number(selectValue);
    if (this.props.checkStatus) {
      this.props.checkStatus(this.selectValue);
    }
  };

  render() {
    return (
      <div className='status-change'>
        <div className='status-change-message'>{data.message.changeStatus}</div>
        <div className='status-change-radio'>
          <label>
            <input
              type='radio'
              name='worker_status'
              value={WORKER_STATUS.IN_OPERATION.ID}
              checked={WORKER_STATUS.IN_OPERATION.ID == this.selectValue}
              onChange={e => this.checkStatus(e.target.value)}
            />
            <span>{WORKER_STATUS.IN_OPERATION.NAME}</span>
          </label>
          <label>
            <input
              type='radio'
              name='worker_status'
              value={WORKER_STATUS.WORK_DONE.ID}
              checked={WORKER_STATUS.WORK_DONE.ID == this.selectValue}
              onChange={e => this.checkStatus(e.target.value)}
            />
            <span>{WORKER_STATUS.WORK_DONE.NAME}</span>
          </label>
        </div>
      </div>
    );
  }
}

// 定数
const data = {
  tableTitle: {
    fullName: '作業員名',
    status: 'ステータス',
  },
  statusDialog: {
    title: 'ステータスの変更',
    buttonTitle: {
      update: '変更',
      back: '戻る',
    },
  },
  message: {
    statusInfo: '退職の作業員はステータスを変更できません。',
    changeStatus:
      'ステータスを「作業終了」にすると、その作業員の入退場実績が記録できなくなります。',
  },
};



// WEBPACK FOOTER //
// ./src/components/FieldTreeGeneralWorkerManager.jsx